import React from 'react';
import { X } from 'lucide-react';
import { SpreadsheetCell } from './spreadsheet-cell';
import type { SpreadsheetRowProps } from './types';

/**
 * SpreadsheetRow Component
 *
 * Renders a single row with:
 * - Label (with indentation)
 * - Cells for each period
 * - Bold styling for totals
 * - Separator borders
 */
export const SpreadsheetRow: React.FC<SpreadsheetRowProps> = ({
  row,
  periods,
  onCellChange,
  readOnly,
  focusedCell,
  rowIndex,
  onNavigate,
  onDeleteRow
}) => {
  /**
   * Determine if cell is editable
   */
  const isCellEditable = (rowType: string): boolean => {
    if (readOnly) return false;
    return rowType === 'input';
  };

  /**
   * Handle cell value change
   */
  const handleCellSave = (periodId: string, value: number) => {
    return onCellChange(row.id, periodId, value);
  };

  /**
   * Row label styles with indentation
   */
  const getLabelStyles = (): string => {
    const baseStyles = 'px-3 py-1 text-left border border-gray-200 bg-white';
    const indentPadding = row.indent ? `pl-${(row.indent + 1) * 6}` : 'pl-3';
    const fontWeight = row.bold ? 'font-bold' : 'font-normal';

    return `${baseStyles} ${indentPadding} ${fontWeight}`;
  };

  /**
   * Row container styles with separator
   */
  const getRowStyles = (): string => {
    const baseStyles = 'grid grid-cols-[280px_repeat(auto-fit,_minmax(140px,_1fr))]';

    if (row.separator === 'above') {
      return `${baseStyles} border-t-2 border-gray-400`;
    }

    if (row.separator === 'below') {
      return `${baseStyles} border-b-2 border-gray-400`;
    }

    return baseStyles;
  };

  /**
   * Get cell value for period (handle calculated rows)
   */
  const getCellValue = (periodId: string): number | null => {
    // Values are pre-calculated in panel useMemo for all row types
    return row.values[periodId] === null ? null : row.values[periodId] || 0;
  };

  /**
   * Subheader rows render as a full-width bold label with grey background
   * No editable cells, not focusable, used for OpEx subcategory headers
   */
  if (row.type === 'subheader') {
    const indentPadding = row.indent ? `pl-${(row.indent + 1) * 6}` : 'pl-3';
    return (
      <div
        className="grid grid-cols-[280px_1fr]"
        data-testid={`spreadsheet-row-${row.id}`}
        style={{ minHeight: '28px' }}
      >
        <div
          className={`${indentPadding} py-1 font-bold bg-gray-100 border border-gray-200 col-span-full`}
          data-testid={`subheader-label-${row.id}`}
        >
          {row.label}
        </div>
      </div>
    );
  }

  return (
    <div
      className={getRowStyles()}
      data-testid={`spreadsheet-row-${row.id}`}
      style={{ minHeight: '28px' }}
    >
      {/* Row label */}
      <div className={`${getLabelStyles()} flex items-center justify-between`}>
        <span>{row.label}</span>
        {row.isCustom && onDeleteRow && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDeleteRow(row.customLineItemId!);
            }}
            className="text-muted-foreground hover:text-destructive"
            data-testid={`delete-row-${row.id}`}
            aria-label="Delete custom line item"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Cell for each period */}
      {periods.map((period, columnIndex) => {
        if (row.type !== 'input' && row.values[period.periodId] === null && row.unavailableText) {
          return <div key={`${row.id}-${period.periodId}`} role="status">{row.unavailableText}</div>;
        }
        const isFocused =
          focusedCell?.rowIndex === rowIndex &&
          focusedCell?.columnIndex === columnIndex;

        return (
          <SpreadsheetCell
            key={`${row.id}-${period.periodId}`}
            rowId={row.id}
            periodId={period.periodId}
            value={getCellValue(period.periodId)}
            editable={isCellEditable(row.type)}
            onSave={(value) => handleCellSave(period.periodId, value)}
            onNavigate={(direction, autoEditFlag) => {
              onNavigate({ rowIndex, columnIndex }, direction, autoEditFlag);
            }}
            isFocused={isFocused}
            autoEdit={isFocused ? focusedCell?.autoEdit : false}
          />
        );
      })}
    </div>
  );
};
